import React, { useState } from "react";
import { Lightbulb, Plus, Pencil, Trash2, CheckCircle, Check, X, Loader2, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { UserAvatar } from "../shared/UserAvatar";
import { toast } from "sonner";
import * as api from "../../lib/api";

export function TicketSolutions({ ticket, solutions = [], onUpdate, currentUser, isAgentOrAdmin, getUserById, aiEnabled }) {
    const [newContent, setNewContent] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editContent, setEditContent] = useState("");
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [reformulating, setReformulating] = useState(null); // "new" | solution_id
    const [retaining, setRetaining] = useState(null);

    // Sort: retained first, then by date
    const sorted = [...solutions].sort((a, b) => {
        if (a.is_retained && !b.is_retained) return -1;
        if (!a.is_retained && b.is_retained) return 1;
        return new Date(a.created_at) - new Date(b.created_at);
    });

    const retainedCount = solutions.filter((s) => s.is_retained).length;

    const handleCreate = async () => {
        if (!newContent.trim()) return;
        try {
            setSubmitting(true);
            await api.createSolution(ticket.id, { content: newContent }, currentUser.id);
            setNewContent("");
            toast.success("Solution ajoutee");
            onUpdate();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        finally { setSubmitting(false); }
    };

    const handleUpdate = async (solId) => {
        if (!editContent.trim()) return;
        try {
            await api.updateSolution(solId, { content: editContent }, currentUser.id);
            setEditingId(null);
            toast.success("Solution modifiee");
            onUpdate();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleDelete = async (solId) => {
        try {
            await api.deleteSolution(solId, currentUser.id);
            setDeleteConfirm(null);
            toast.success("Solution supprimee");
            onUpdate();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleToggleRetain = async (solId) => {
        try {
            setRetaining(solId);
            await api.toggleRetainSolution(solId, currentUser.id);
            onUpdate();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        finally { setRetaining(null); }
    };

    const handleReformulate = async (text, target) => {
        if (!text.trim()) return;
        try {
            setReformulating(target);
            const result = await api.reformulateComment({
                comment: text,
                context: "solution",
                ticket_number: ticket.ticket_number,
                ticket_title: ticket.title,
            });
            if (target === "new") {
                setNewContent(result.reformulated);
            } else {
                setEditContent(result.reformulated);
            }
            toast.success("Texte reformule par l'IA");
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur IA"); }
        finally { setReformulating(null); }
    };

    // Users: don't show section if no solutions
    if (!isAgentOrAdmin && solutions.length === 0) return null;

    return (
        <div className="card" data-testid="solutions-section">
            <div className="card-body">
                {/* Header */}
                <div className="flex items-center gap-2 mb-4">
                    <Lightbulb className="w-5 h-5 text-amber-500" />
                    <h3 className="text-base font-semibold">Solutions</h3>
                    {solutions.length > 0 && (
                        <span className="text-xs text-gray-500">
                            ({solutions.length} solution{solutions.length > 1 ? "s" : ""}
                            {retainedCount > 0 ? `, ${retainedCount} retenue` : ""})
                        </span>
                    )}
                </div>

                {/* Solutions list */}
                {sorted.length > 0 ? (
                    <div className="space-y-3 mb-4">
                        {sorted.map((sol) => {
                            const author = getUserById(sol.author_id);
                            const isEditing = editingId === sol.id;
                            return (
                                <div
                                    key={sol.id}
                                    className={`rounded-lg border p-3 transition-colors ${sol.is_retained ? "bg-green-50 border-green-200" : "bg-white border-gray-200"}`}
                                    data-testid={`solution-${sol.id}`}
                                >
                                    {/* Retained badge */}
                                    {sol.is_retained && (
                                        <div className="flex items-center gap-1.5 mb-2" data-testid={`solution-retained-badge-${sol.id}`}>
                                            <CheckCircle className="w-4 h-4 text-green-600" />
                                            <span className="text-xs font-semibold text-green-700">Solution retenue</span>
                                        </div>
                                    )}

                                    {/* Content */}
                                    {isEditing ? (
                                        <div className="space-y-2">
                                            <Textarea
                                                value={editContent}
                                                onChange={(e) => setEditContent(e.target.value)}
                                                className="min-h-[80px]"
                                                data-testid={`solution-edit-textarea-${sol.id}`}
                                            />
                                            <div className="flex items-center gap-2">
                                                {aiEnabled && (
                                                    <Button type="button" variant="outline" size="sm"
                                                        onClick={() => handleReformulate(editContent, sol.id)}
                                                        disabled={!editContent.trim() || reformulating === sol.id}
                                                        className="gap-1.5 text-purple-600 border-purple-200 hover:bg-purple-50"
                                                        data-testid={`solution-ai-edit-${sol.id}`}>
                                                        {reformulating === sol.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                                                        Reformuler
                                                    </Button>
                                                )}
                                                <div className="ml-auto flex gap-1.5">
                                                    <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} data-testid={`solution-cancel-edit-${sol.id}`}>
                                                        <X className="w-3.5 h-3.5" />
                                                    </Button>
                                                    <Button size="sm" onClick={() => handleUpdate(sol.id)} disabled={!editContent.trim()} data-testid={`solution-save-edit-${sol.id}`}>
                                                        <Check className="w-3.5 h-3.5 mr-1" /> Sauvegarder
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-800 whitespace-pre-wrap" data-testid={`solution-content-${sol.id}`}>{sol.content}</p>
                                    )}

                                    {/* Footer: author + actions */}
                                    {!isEditing && (
                                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                                            <div className="flex items-center gap-2">
                                                <UserAvatar user={author} size="xs" />
                                                <span className="text-xs text-gray-500">
                                                    {author?.first_name} {author?.last_name} — {new Date(sol.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })}
                                                </span>
                                            </div>

                                            {isAgentOrAdmin && (
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        onClick={() => handleToggleRetain(sol.id)}
                                                        disabled={retaining === sol.id}
                                                        className={`p-1.5 rounded-md text-xs transition-colors ${sol.is_retained ? "text-green-600 bg-green-100 hover:bg-green-200" : "text-gray-400 hover:text-green-600 hover:bg-green-50"}`}
                                                        title={sol.is_retained ? "Retirer le marquage" : "Marquer comme retenue"}
                                                        data-testid={`solution-retain-${sol.id}`}
                                                    >
                                                        {retaining === sol.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                                    </button>
                                                    <button
                                                        onClick={() => { setEditingId(sol.id); setEditContent(sol.content); }}
                                                        className="p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                                        title="Modifier"
                                                        data-testid={`solution-edit-${sol.id}`}
                                                    >
                                                        <Pencil className="w-3.5 h-3.5" />
                                                    </button>
                                                    {aiEnabled && (
                                                        <button
                                                            onClick={() => { setEditingId(sol.id); setEditContent(sol.content); setTimeout(() => handleReformulate(sol.content, sol.id), 100); }}
                                                            disabled={reformulating === sol.id}
                                                            className="p-1.5 rounded-md text-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
                                                            title="Reformuler avec l'IA"
                                                            data-testid={`solution-ai-${sol.id}`}
                                                        >
                                                            {reformulating === sol.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                                                        </button>
                                                    )}
                                                    {deleteConfirm === sol.id ? (
                                                        <div className="flex items-center gap-1 ml-1">
                                                            <Button size="sm" variant="destructive" onClick={() => handleDelete(sol.id)} className="h-6 px-2 text-xs" data-testid={`solution-confirm-delete-${sol.id}`}>Oui</Button>
                                                            <Button size="sm" variant="ghost" onClick={() => setDeleteConfirm(null)} className="h-6 px-2 text-xs">Non</Button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => setDeleteConfirm(sol.id)}
                                                            className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                                            title="Supprimer"
                                                            data-testid={`solution-delete-${sol.id}`}
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : isAgentOrAdmin ? (
                    <p className="text-sm text-gray-500 mb-4">Aucune solution documentee. Ajoutez la premiere solution.</p>
                ) : null}

                {/* Add solution form — agents/admins only */}
                {isAgentOrAdmin && (
                    <div className="space-y-2 pt-2 border-t border-gray-100" data-testid="solution-add-form">
                        <Textarea
                            placeholder="Ajouter une solution..."
                            value={newContent}
                            onChange={(e) => setNewContent(e.target.value)}
                            className="min-h-[80px]"
                            data-testid="solution-new-textarea"
                        />
                        <div className="flex items-center gap-2">
                            {aiEnabled && (
                                <Button type="button" variant="outline" size="sm"
                                    onClick={() => handleReformulate(newContent, "new")}
                                    disabled={!newContent.trim() || reformulating === "new"}
                                    className="gap-1.5 text-purple-600 border-purple-200 hover:bg-purple-50"
                                    data-testid="solution-ai-new">
                                    {reformulating === "new" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                                    Reformuler
                                </Button>
                            )}
                            <Button size="sm" onClick={handleCreate} disabled={!newContent.trim() || submitting} className="ml-auto gap-1.5" data-testid="solution-submit-btn">
                                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                                Ajouter la solution
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
